<template>
  <div class="timer-container">
    <div v-if="isActive" class="timer-active">
      <div class="timer-display" :class="{ 'timer-warning': timeLeft <= warningThreshold }">
        <svg class="timer-icon" viewBox="0 0 24 24" width="24" height="24">
          <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c.55 0 1 .45 1 1v5.5l3.15 3.15c.39.39.39 1.02 0 1.41-.39.39-1.02.39-1.41 0l-3.5-3.5c-.19-.19-.3-.44-.3-.7V7c0-.55.45-1 1-1z"/>
        </svg>
        <span class="timer-time">{{ formattedTime }}</span>
      </div>
      <div class="timer-progress">
        <div
          class="timer-progress-bar"
          :style="{ width: `${progressPercent}%` }"
          :class="{ 'timer-progress-warning': timeLeft <= warningThreshold }"
        ></div>
      </div>
    </div>

    <div v-else-if="isExpired" class="timer-expired">
      <svg class="timer-icon" viewBox="0 0 24 24" width="20" height="20">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
      <span>Время ожидания истекло</span>
    </div>

    <button
      v-else
      @click="startTimer"
      :disabled="isLoading"
      class="timer-start-btn"
      :class="{ 'timer-start-loading': isLoading }"
    >
      <span v-if="!isLoading">⏱️ Начать ожидание</span>
      <span v-else>Запуск...</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'Timer',

  props: {
    duration: {
      type: Number,
      required: true,
      validator: (value) => value > 0
    },
    warningThreshold: {
      type: Number,
      default: 120
    },
  },

  emits: ['start', 'tick', 'expired', 'cancel', 'complete'],

  data() {
    return {
      timeLeft: this.duration,
      isActive: false,
      isExpired: false,
      isLoading: false,
      timerInterval: null,
      startTime: null
    }
  },

  computed: {
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60)
      const seconds = this.timeLeft % 60

      if (minutes > 0) {
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
      }
      return `${seconds} сек`
    },

    progressPercent() {
      return (this.timeLeft / this.duration) * 100
    }
  },

  watch: {
    duration(newDuration) {
      if (!this.isActive && !this.isExpired) {
        this.timeLeft = newDuration
      }
    }
  },

  mounted() {
      this.startTimer()
  },

  beforeUnmount() {
    this.clearTimer()
  },

  methods: {
    startTimer() {
      if (this.isActive) return

      this.isLoading = true
      this.clearTimer()

      this.timeLeft = this.duration
      this.isActive = true
      this.isExpired = false
      this.startTime = Date.now()

      this.$emit('start', { duration: this.duration })

      setTimeout(() => {
        this.isLoading = false
        this.runTimer()
      }, 300)
    },

    runTimer() {
      this.timerInterval = setInterval(() => {
        if (!this.isActive) return

        const now = Date.now()
        const elapsed = Math.floor((now - this.startTime) / 1000)
        const remaining = this.duration - elapsed

        this.timeLeft = Math.max(0, remaining)

        this.$emit('tick', { timeLeft: this.timeLeft, progress: this.progressPercent })

        if (this.timeLeft <= 0) {
          this.expireTimer()
        }
      }, 100)
    },

    expireTimer() {
      this.clearTimer()
      this.isActive = false
      this.isExpired = true

      this.$emit('expired')
      this.$emit('complete', { success: false, reason: 'timeout' })
    },

    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    }
  }
}
</script>

<style scoped>
.timer-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.timer-active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 20px;
  color: white;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}

.timer-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 2.5em;
  font-weight: bold;
  margin-bottom: 16px;
}

.timer-warning {
  color: #ffeb3b;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.timer-icon {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.timer-progress {
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 16px;
}

.timer-progress-bar {
  background: white;
  height: 100%;
  transition: width 0.1s linear;
  border-radius: 10px;
}

.timer-progress-warning {
  background: #ffeb3b;
}

.timer-cancel-btn {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.timer-cancel-btn:hover {
  background: rgba(255,255,255,0.3);
  transform: translateY(-1px);
}

.timer-expired {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  color: #c33;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.timer-start-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.timer-start-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.timer-start-loading {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>